import { useState } from "react";
import { Upload, Loader2, FileUp, Sparkles, Search, Building2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY = { doc_type: "sluttseddel", share_class: "", shares: "", price_per_share: "", seller_name: "", seller_org: "", buyer_name: "", buyer_org: "", note: "" };

export function UploadAgreementDialog({ companyId, rows = [], classes = [], onUploaded, open, onOpenChange }) {
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [proffQ, setProffQ] = useState("");
  const [proffBusy, setProffBusy] = useState(false);
  const [proffResults, setProffResults] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const classNames = classes.map((c) => c.name).filter(Boolean);

  const reset = () => { setForm(EMPTY); setFile(null); setProffQ(""); setProffResults(null); };

  const runExtract = async () => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setAiBusy(true);
    try {
      const { data } = await api.post(`/companies/${companyId}/share-uploads/extract`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({
        ...f,
        doc_type: data.doc_type || f.doc_type,
        share_class: data.share_class || f.share_class,
        shares: data.shares != null ? String(data.shares) : f.shares,
        price_per_share: data.price_per_share != null ? String(data.price_per_share) : f.price_per_share,
        seller_name: data.seller_name || f.seller_name,
        seller_org: data.seller_org || f.seller_org,
        buyer_name: data.buyer_name || f.buyer_name,
        buyer_org: data.buyer_org || f.buyer_org,
        note: data.note || f.note,
      }));
      toast.success("Feltene er fylt ut fra dokumentet – kontroller før du sender");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setAiBusy(false);
    }
  };

  const proffSearch = async () => {
    const q = proffQ.trim();
    if (q.length < 2) return toast.error("Skriv minst 2 tegn");
    setProffBusy(true);
    try {
      const { data } = await api.get(`/proff/search`, { params: { q } });
      setProffResults(data.companies || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setProffBusy(false);
    }
  };

  const pickProff = (r, role) => {
    if (role === "seller") setForm((f) => ({ ...f, seller_name: r.name, seller_org: r.org_number || "" }));
    else setForm((f) => ({ ...f, buyer_name: r.name, buyer_org: r.org_number || "" }));
    toast.success(`${r.name} satt som ${role === "seller" ? "selger" : "kjøper"}`);
  };

  const submit = async () => {
    if (!file) return toast.error("Velg en PDF-fil å laste opp");
    const fd = new FormData();
    fd.append("file", file);
    Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ""));
    setSaving(true);
    try {
      await api.post(`/companies/${companyId}/share-uploads`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Sendt til Murphy for godkjenning");
      onOpenChange(false);
      reset();
      onUploaded?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="upload-agreement-dialog">
        <DialogHeader>
          <DialogTitle>Last opp ferdig avtale / sluttseddel</DialogTitle>
          <DialogDescription>Last opp et signert dokument. La gjerne AI lese ut feltene, eller hent motparten fra Proff. Murphy godkjenner og registrerer transaksjonen.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Dokument (PDF)</Label>
            <label className="mt-1.5 flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-border px-3 py-3 text-sm hover:bg-muted/40" data-testid="upload-file-label">
              <FileUp className="h-4 w-4 shrink-0 text-primary" />
              <span className="truncate text-muted-foreground">{file ? file.name : "Velg PDF-fil …"}</span>
              <input type="file" accept="application/pdf,.pdf" className="hidden" data-testid="upload-file-input" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </label>
            {file ? (
              <Button type="button" size="sm" variant="secondary" className="mt-2 gap-1.5" disabled={aiBusy} data-testid="upload-ai-extract-btn" onClick={runExtract}>
                {aiBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {aiBusy ? "Leser dokumentet …" : "Les ut felter med AI"}
              </Button>
            ) : null}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Type</Label>
              <Select value={form.doc_type} onValueChange={(v) => set("doc_type", v)}>
                <SelectTrigger className="mt-1.5" data-testid="upload-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sluttseddel">Sluttseddel</SelectItem>
                  <SelectItem value="avtale">Aksjekjøpsavtale (SPA)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Aksjeklasse</Label>
              {classNames.length ? (
                <Select value={form.share_class} onValueChange={(v) => set("share_class", v)}>
                  <SelectTrigger className="mt-1.5" data-testid="upload-class-select"><SelectValue placeholder="Velg" /></SelectTrigger>
                  <SelectContent>{classNames.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              ) : (
                <Input className="mt-1.5" value={form.share_class} onChange={(e) => set("share_class", e.target.value)} placeholder="Ordinære aksjer" />
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Antall aksjer</Label><Input type="number" className="mt-1.5" data-testid="upload-shares-input" value={form.shares} onChange={(e) => set("shares", e.target.value)} /></div>
            <div><Label>Kurs per aksje (kr)</Label><Input type="number" className="mt-1.5" data-testid="upload-price-input" value={form.price_per_share} onChange={(e) => set("price_per_share", e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Selger</Label>
              <Input className="mt-1.5" list="upload-sellers" data-testid="upload-seller-input" value={form.seller_name} onChange={(e) => set("seller_name", e.target.value)} placeholder="Navn" />
              {form.seller_org ? <p className="mt-1 text-[11px] text-muted-foreground" data-testid="upload-seller-org">Org.nr: {form.seller_org}</p> : null}
            </div>
            <div>
              <Label>Kjøper</Label>
              <Input className="mt-1.5" data-testid="upload-buyer-input" value={form.buyer_name} onChange={(e) => set("buyer_name", e.target.value)} placeholder="Navn" />
              {form.buyer_org ? <p className="mt-1 text-[11px] text-muted-foreground" data-testid="upload-buyer-org">Org.nr: {form.buyer_org}</p> : null}
            </div>
          </div>
          <datalist id="upload-sellers">{rows.map((r) => <option key={r.id} value={r.name} />)}</datalist>

          <div className="rounded-lg border border-border/60 bg-muted/20 p-3">
            <Label className="flex items-center gap-1.5 text-xs"><Building2 className="h-3.5 w-3.5 text-primary" /> Hent motpart fra Proff</Label>
            <div className="mt-1.5 flex gap-2">
              <Input value={proffQ} data-testid="proff-search-input" placeholder="Selskapsnavn eller org.nr" onChange={(e) => setProffQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); proffSearch(); } }} />
              <Button type="button" variant="outline" className="shrink-0 gap-1.5" disabled={proffBusy} data-testid="proff-search-btn" onClick={proffSearch}>
                {proffBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Søk
              </Button>
            </div>
            {proffResults ? (
              <div className="mt-2 max-h-44 space-y-1.5 overflow-y-auto" data-testid="proff-results">
                {proffResults.length === 0 ? <p className="text-xs text-muted-foreground">Ingen treff i Proff.</p> :
                  proffResults.map((r) => (
                    <div key={r.org_number} className="flex items-center gap-2 rounded-md border border-border/50 bg-card px-2.5 py-1.5 text-xs">
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium">{r.name}</p>
                        <p className="text-[11px] text-muted-foreground">Org.nr {r.org_number}</p>
                      </div>
                      <Button type="button" size="sm" variant="ghost" className="h-6 px-2 text-[11px]" data-testid={`proff-set-seller-${r.org_number}`} onClick={() => pickProff(r, "seller")}>Som selger</Button>
                      <Button type="button" size="sm" variant="ghost" className="h-6 px-2 text-[11px]" data-testid={`proff-set-buyer-${r.org_number}`} onClick={() => pickProff(r, "buyer")}>Som kjøper</Button>
                    </div>
                  ))}
              </div>
            ) : null}
          </div>

          <div><Label>Merknad (valgfritt)</Label><Textarea className="mt-1.5" rows={2} value={form.note} onChange={(e) => set("note", e.target.value)} /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Avbryt</Button>
          <Button onClick={submit} disabled={saving} className="gap-2" data-testid="upload-submit-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Send til godkjenning
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
